// Mapa 3D del campo: cada capa es una métrica de las estaciones del campo,
// interpolada entre ellas y dibujada como un volumen sobre el lote (más alto
// = más valor). La altura es relativa a lo que marcan las estaciones en ese
// momento — así se ve cómo varía dentro del campo (una zona más húmeda que
// otra), que es para lo que sirve el mapa.
export type FieldLayer = 'temperature' | 'humidity' | 'soil' | 'rain' | 'wind';

export interface FieldLayerConfig {
  label: string;
  unit: string;
  decimals: number;
  // Diferencia mínima que se dibuja como "de abajo a arriba": si las
  // estaciones casi no difieren (21,1 vs 21,3 °C), no se exagera el relieve.
  minSpan: number;
  // Escala de color del valor más bajo al más alto del rango, en tramos
  // parejos. Con más de dos colores el medio no queda grisáceo.
  ramp: string[];
}

export const FIELD_LAYERS: FieldLayer[] = ['soil', 'humidity', 'temperature', 'rain', 'wind'];

export const FIELD_LAYER_CONFIG: Record<FieldLayer, FieldLayerConfig> = {
  // Seco (tierra) → húmedo (agua).
  soil: { label: 'Humedad de suelo', unit: '%', decimals: 0, minSpan: 10, ramp: ['#c2702b', '#f0cf5f', '#4fbf9f', '#1e5fc2'] },
  humidity: { label: 'Humedad', unit: '%', decimals: 0, minSpan: 10, ramp: ['#d9a441', '#f2e08a', '#6cc4c9', '#2563c9'] },
  // Frío → calor.
  temperature: { label: 'Temperatura', unit: '°C', decimals: 1, minSpan: 3, ramp: ['#2c7bd1', '#7fd0c3', '#f3d36b', '#d9472b'] },
  rain: { label: 'Lluvia 24 h', unit: 'mm', decimals: 1, minSpan: 5, ramp: ['#d6ecf8', '#7cbde8', '#2f78d0', '#1b3a9e'] },
  wind: { label: 'Viento', unit: 'km/h', decimals: 0, minSpan: 8, ramp: ['#e3e8ec', '#a7b4bf', '#5d6b78', '#2f3a45'] },
};

export type FieldValues = Record<FieldLayer, number | null>;

// Lo que devuelve GET /sites/:id/field: cada estación del campo con
// posición y su valor actual (del último día) en todas las capas.
export interface FieldStation {
  id: string;
  name: string;
  status: 'online' | 'offline';
  lat: number;
  lng: number;
  values: FieldValues;
  recordedAt: string | null;
}
export function formatFieldValue(layer: FieldLayer, value: number): string {
  const { decimals, unit } = FIELD_LAYER_CONFIG[layer];
  return `${value.toFixed(decimals).replace('.', ',')} ${unit}`;
}

// Rango que va de la altura mínima a la máxima: el de las lecturas, abierto
// hasta minSpan alrededor del centro si son muy parecidas.
export function fieldRange(layer: FieldLayer, values: number[]): [number, number] | null {
  if (values.length === 0) return null;
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const span = Math.max(hi - lo, FIELD_LAYER_CONFIG[layer].minSpan);
  const mid = (lo + hi) / 2;
  return [mid - span / 2, mid + span / 2];
}

// Color de un punto t (0..1) de la escala de la capa, como "#rrggbb".
export function rampColor(layer: FieldLayer, t: number): string {
  const stops = FIELD_LAYER_CONFIG[layer].ramp.map((hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)));
  const pos = Math.min(1, Math.max(0, t)) * (stops.length - 1);
  const k = Math.min(stops.length - 2, Math.floor(pos));
  const [from, to] = [stops[k], stops[k + 1]];
  const mix = from.map((c, i) => Math.round(c + (to[i] - c) * (pos - k)));
  return `#${mix.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

export function normalizeInRange(value: number, [lo, hi]: [number, number]): number {
  return Math.min(1, Math.max(0, (value - lo) / (hi - lo)));
}

export interface FieldPoint {
  x: number;
  z: number;
  value: number;
}

// Interpolación por distancia inversa (IDW): cada punto del campo toma el
// promedio de las estaciones pesado por 1/d². Con una sola estación, todo el
// campo vale lo mismo; sobre una estación, vale exactamente su lectura.
export function interpolate(points: FieldPoint[], x: number, z: number): number | null {
  if (points.length === 0) return null;
  let weighted = 0;
  let total = 0;
  for (const p of points) {
    const d2 = (p.x - x) ** 2 + (p.z - z) ** 2;
    if (d2 < 1e-6) return p.value;
    const w = 1 / d2;
    weighted += w * p.value;
    total += w;
  }
  return weighted / total;
}

// --- Geografía: el campo en metros alrededor de un centro ---

export interface LatLng {
  lat: number;
  lng: number;
}

export interface LocalPoint {
  // Metros al este (x) y al sur (z) del centro: en three.js la cámara mira
  // hacia -z, el norte queda "al fondo".
  x: number;
  z: number;
}

export interface FieldGeometry {
  center: LatLng;
  // Medio ancho (este-oeste) y medio alto (norte-sur) del terreno, en metros.
  halfWidth: number;
  halfDepth: number;
  // Contorno del lote (convexo, en orden): envuelve a todas las estaciones
  // con un margen redondeado. El volumen se dibuja solo adentro.
  footprint: LocalPoint[];
}

const METERS_PER_DEG_LAT = 111_320;
// Radio alrededor de cada estación: una fracción de lo que abarcan juntas,
// así 10 estaciones en un lote no quedan perdidas en un rectángulo enorme
// y una sola no ocupa medio kilómetro.
const RADIUS_FRACTION = 0.18;
const MIN_RADIUS = 30;
const MAX_RADIUS = 120;
const SINGLE_STATION_RADIUS = 60;
const CIRCLE_SEGMENTS = 16;
// Terreno visible alrededor del lote.
const TERRAIN_MARGIN = 1.25;

export function toLocal(center: LatLng, p: LatLng): LocalPoint {
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos((center.lat * Math.PI) / 180);
  return { x: (p.lng - center.lng) * metersPerDegLng, z: -(p.lat - center.lat) * METERS_PER_DEG_LAT };
}

export function toLatLng(center: LatLng, x: number, z: number): LatLng {
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos((center.lat * Math.PI) / 180);
  return { lat: center.lat - z / METERS_PER_DEG_LAT, lng: center.lng + x / metersPerDegLng };
}

export function fieldGeometry(points: LatLng[]): FieldGeometry | null {
  if (points.length === 0) return null;
  const lats = points.map((p) => p.lat);
  const lngs = points.map((p) => p.lng);
  const center = { lat: (Math.min(...lats) + Math.max(...lats)) / 2, lng: (Math.min(...lngs) + Math.max(...lngs)) / 2 };
  const local = points.map((p) => toLocal(center, p));

  const xs = local.map((p) => p.x);
  const zs = local.map((p) => p.z);
  const extent = Math.hypot(Math.max(...xs) - Math.min(...xs), Math.max(...zs) - Math.min(...zs));
  const radius = extent < 1 ? SINGLE_STATION_RADIUS : Math.min(MAX_RADIUS, Math.max(MIN_RADIUS, extent * RADIUS_FRACTION));

  const ring: LocalPoint[] = [];
  for (const p of local) {
    for (let i = 0; i < CIRCLE_SEGMENTS; i++) {
      const a = (2 * Math.PI * i) / CIRCLE_SEGMENTS;
      ring.push({ x: p.x + radius * Math.cos(a), z: p.z + radius * Math.sin(a) });
    }
  }
  const footprint = convexHull(ring);
  const halfWidth = Math.max(...footprint.map((p) => Math.abs(p.x))) * TERRAIN_MARGIN;
  const halfDepth = Math.max(...footprint.map((p) => Math.abs(p.z))) * TERRAIN_MARGIN;
  return { center, halfWidth, halfDepth, footprint };
}

// Envolvente convexa (monotone chain), en sentido antihorario.
export function convexHull(points: LocalPoint[]): LocalPoint[] {
  const sorted = [...points].sort((a, b) => a.x - b.x || a.z - b.z);
  const cross = (o: LocalPoint, a: LocalPoint, b: LocalPoint) => (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);
  const half = (list: LocalPoint[]) => {
    const out: LocalPoint[] = [];
    for (const p of list) {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop();
      out.push(p);
    }
    out.pop();
    return out;
  };
  return [...half(sorted), ...half([...sorted].reverse())];
}

// Punto del polígono (convexo) más cercano a p; p mismo si ya está adentro.
export function clampToPolygon(polygon: LocalPoint[], p: LocalPoint): LocalPoint {
  let inside = true;
  let best = p;
  let bestD = Infinity;
  for (let i = 0; i < polygon.length; i++) {
    const a = polygon[i];
    const b = polygon[(i + 1) % polygon.length];
    const ex = b.x - a.x;
    const ez = b.z - a.z;
    if (ex * (p.z - a.z) - ez * (p.x - a.x) < 0) inside = false;
    const t = Math.max(0, Math.min(1, ((p.x - a.x) * ex + (p.z - a.z) * ez) / (ex * ex + ez * ez)));
    const q = { x: a.x + t * ex, z: a.z + t * ez };
    const d = (q.x - p.x) ** 2 + (q.z - p.z) ** 2;
    if (d < bestD) {
      bestD = d;
      best = q;
    }
  }
  return inside ? p : best;
}
